import { useEffect, useState } from 'react';
import { Button, Container } from 'reactstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  IconArrowLeft,
  IconCalendar,
  IconCheck,
  IconClock,
  IconHeart,
  IconHome,
  IconLock,
  IconMapPin,
  IconUsers,
} from '@tabler/icons-react';
import { PropersAssignedVisits } from './models/dashboardModels';
import { VISIT_STATUS } from '../../constants/visitsConstants';
import {
  getBadgeColorVisitStatusAdapter,
  getVisitStatusAdapter,
} from '../properties/adapters/visitsAdapter';
import { getStringDate } from '../../adapters/dateAdapter';
import useProperList from '../../hooks/useProperList';
import useRentVisitActions from './hooks/useRentVisitActions';
import useVisitFeedback from './hooks/useVisitFeedback';
import VisitFeedbackCard from './components/VisitFeedbackCard';
import { Field, Section } from './components/VisitDetailParts';
import IntentSignalsCell from './components/IntentSignalsCell';
import InvestorFavoritesModal from './components/InvestorFavoritesModal';
import ActivateGuestModal from '../guest/components/ActivateGuestModal';
import VisitMarketplaceTypeBadge, {
  LeadMarketplaceType,
} from '../../components/VisitMarketplaceTypeBadge';
import { visitTypeOptions } from '../investors/constants/visitConstants';

interface LocationState {
  visit?: PropersAssignedVisits;
}

type Panel = 'accept' | 'schedule' | 'cancel' | null;

const STEPS = [
  { key: VISIT_STATUS.PENDING_APPROVAL, label: 'Pendiente de aprobación' },
  { key: VISIT_STATUS.SCHEDULED, label: 'Programada' },
  { key: VISIT_STATUS.DONE, label: 'Finalizada' },
];

const getCurrentStep = (status: string): number => {
  switch (status) {
    case VISIT_STATUS.PENDING_APPROVAL:
      return 0;
    case VISIT_STATUS.SCHEDULED:
    case VISIT_STATUS.RESCHEDULED:
      return 1;
    case VISIT_STATUS.DONE:
      return 2;
    default:
      return -1; // cancelada / eliminada
  }
};

const RentVisitDetail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // La lista no tiene un endpoint para pedir una visita por id: el detalle se
  // abre con la visita que ya cargó el dashboard (router state).
  const visit = (location.state as LocationState | null)?.visit;

  const marketplaceType = visit?.visitInfo.marketplace_type;
  const { propersOptions } = useProperList(
    marketplaceType === LeadMarketplaceType.ON_RENT ? 'for_rent' : 'for_sale',
  );
  const actions = useRentVisitActions(visit);
  // Feedback del Proper (sólo lectura para el admin).
  const savedFeedback = useVisitFeedback(
    visit?.id,
    visit?.visitInfo.status === VISIT_STATUS.DONE &&
      visit?.visitInfo.has_feedback !== false,
  );

  const [panel, setPanel] = useState<Panel>(null);
  const [activateOpen, setActivateOpen] = useState(false);
  const [favoritesOpen, setFavoritesOpen] = useState(false);
  const [visitType, setVisitType] = useState('virtual');
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [properId, setProperId] = useState('');
  const [cancelReason, setCancelReason] = useState('');
  const [cancelExplain, setCancelExplain] = useState('');

  useEffect(() => {
    if (!actions.guestVisit) return;
    setVisitType(actions.guestVisit.visitInfo.visit_type || 'virtual');
    setProperId(actions.guestVisit.visitInfo.proper_initial_selected_id ?? '');
  }, [actions.guestVisit]);

  // Precarga la fecha y hora con las que ya está agendada la visita.
  useEffect(() => {
    if (!visit) return;
    setDate(actions.original.date);
    setTime(
      actions.original.minutes !== undefined
        ? String(actions.original.minutes)
        : '',
    );
    actions.loadTimeSlots(
      actions.original.date,
      visit.visitInfo.status === VISIT_STATUS.PENDING_APPROVAL
        ? 'guest'
        : 'admin',
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visit?.id]);

  const goBack = () => navigate(-1);

  if (!visit) {
    return (
      <Container className="mt-6 visit-detail-v2" fluid>
        <Button className="vd-back" onClick={goBack}>
          <IconArrowLeft size={16} /> Volver a visitas
        </Button>
        <section className="vd-card">
          <p className="mb-0 text-muted">
            No se encontró la visita. Ábrela desde la lista del dashboard.
          </p>
        </section>
      </Container>
    );
  }

  const { visitInfo, propertyInfo, properInfo, sdrInfo } = visit;
  const status = visitInfo.status;
  const currentStep = getCurrentStep(status);
  const isDone = status === VISIT_STATUS.DONE;
  const isSale = visitInfo.marketplace_type === LeadMarketplaceType.ON_SALE;
  const isPending = status === VISIT_STATUS.PENDING_APPROVAL;
  const canMarkDone = [VISIT_STATUS.SCHEDULED, VISIT_STATUS.RESCHEDULED].includes(
    status as VISIT_STATUS,
  );
  // Misma condición que el detalle de guest-visit: pendiente, o agendada sin
  // proper asignado todavía.
  const canAccept =
    isPending ||
    (status === VISIT_STATUS.SCHEDULED &&
      !!actions.guestVisit &&
      !actions.guestVisit.visitInfo.proper_initial_selected_id);
  const canAct = canMarkDone || isPending;

  const togglePanel = (next: Exclude<Panel, null>) => {
    actions.setError('');
    setPanel((current) => (current === next ? null : next));
  };

  const handleDateChange = (value: string) => {
    setDate(value);
    setTime('');
    actions.loadTimeSlots(value, isPending ? 'guest' : 'admin');
  };

  const runAccept = async () => {
    setActivateOpen(false);
    const ok = await actions.accept({ properId, visitType, date, time });
    if (ok) goBack();
  };

  const handleAcceptClick = () => {
    if (actions.isGuest) setActivateOpen(true);
    else runAccept();
  };

  const handleSchedule = async () => {
    const ok = isPending
      ? await actions.updateSchedule(visitType, date, time)
      : await actions.reschedule(date, time, properId);
    if (ok) goBack();
  };

  const handleCancel = async () => {
    const ok = isPending
      ? await actions.cancelPending()
      : await actions.cancelScheduled(cancelReason, cancelExplain);
    if (ok) goBack();
  };

  const handleDone = async () => {
    if (await actions.markAsDone()) goBack();
  };

  const renderDateTime = (hint?: string) => (
    <div className="vd-accept__grid">
      <label className="vd-accept__field">
        <span className="vd-field__label">Fecha de la visita</span>
        <input
          type="date"
          className="vd-input"
          value={date}
          min={getStringDate(new Date())}
          onChange={(e) => handleDateChange(e.target.value)}
        />
        <span className="vd-accept__hint">
          Agendada para {visitInfo.visit_date} · {visitInfo.visit_time}.{' '}
          {hint}
        </span>
      </label>
      <label className="vd-accept__field">
        <span className="vd-field__label">Hora de la visita</span>
        <select
          className="vd-input"
          value={time}
          onChange={(e) => setTime(e.target.value)}
          disabled={!date}
        >
          <option value="">
            {!date
              ? 'Elige primero una fecha'
              : actions.timeSlots.length
                ? 'Selecciona una hora'
                : 'Sin horarios disponibles'}
          </option>
          {actions.timeSlots.map((slot) => (
            <option
              key={slot.minutes}
              value={slot.minutes}
              disabled={slot.isBlocked}
            >
              {slot.label}
            </option>
          ))}
        </select>
      </label>
    </div>
  );

  const renderVisitType = () => (
    <div className="vd-accept__field">
      <span className="vd-field__label">Tipo de visita</span>
      <div className="vd-segment" role="group">
        {visitTypeOptions.map((option) => (
          <button
            key={option.value}
            type="button"
            className={visitType === option.value ? 'is-active' : ''}
            aria-pressed={visitType === option.value}
            onClick={() => setVisitType(option.value)}
          >
            {option.label}
          </button>
        ))}
      </div>
    </div>
  );

  const renderProperSelect = () => (
    <label className="vd-accept__field">
      <span className="vd-field__label">Proper asignado</span>
      <select
        className="vd-input"
        value={properId}
        onChange={(e) => setProperId(e.target.value)}
      >
        <option value="">Selecciona un proper</option>
        {propersOptions.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </label>
  );

  const renderPanelHead = (title: string, subtitle: string) => (
    <div className="vd-accept__head">
      <span className="vd-icon vd-icon--green">
        <IconCheck size={18} />
      </span>
      <div>
        <h3 className="vd-panel__title">{title}</h3>
        <p className="vd-accept__sub">{subtitle}</p>
      </div>
    </div>
  );

  const renderFooter = (
    confirmLabel: string,
    onConfirm: () => void,
    disabled: boolean,
    tone: 'primary' | 'solid-warning' | 'solid-danger' = 'primary',
  ) => (
    <div className="vd-accept__footer">
      <button
        type="button"
        className="vd-btn vd-btn--ghost"
        onClick={() => setPanel(null)}
      >
        Descartar
      </button>
      <button
        type="button"
        className={`vd-btn vd-btn--${tone}`}
        onClick={onConfirm}
        disabled={disabled || actions.isSubmitting}
      >
        {confirmLabel}
      </button>
    </div>
  );

  const scheduleChanged =
    date !== actions.original.date ||
    Number(time) !== actions.original.minutes;
  const scheduleReady =
    !!date &&
    time !== '' &&
    scheduleChanged &&
    (isPending ? !!visitType : !!properId);

  return (
    <Container className="mt-6 visit-detail-v2" fluid>
      <Button className="vd-back" onClick={goBack}>
        <IconArrowLeft size={16} /> Volver a visitas
      </Button>

      <section className="vd-card vd-hero">
        <div className="vd-hero__top">
          <span className="vd-icon vd-icon--green vd-icon--lg">
            <IconHome size={26} />
          </span>
          <div>
            <div className="vd-hero__title-row">
              <h1 className="vd-hero__title">{propertyInfo.name}</h1>
              <span
                className={`vd-pill vd-pill--${getBadgeColorVisitStatusAdapter(
                  status,
                )}`}
              >
                {getVisitStatusAdapter(status)}
              </span>
            </div>
            <div className="vd-hero__sub">
              <IconMapPin size={14} /> {propertyInfo.address}
            </div>
            <div className="vd-hero__code">
              [{propertyInfo.code}] · {visit.id}
            </div>
          </div>
        </div>

        {canAct && (
          <div className="vd-actions">
            {canAccept && (
              <button
                type="button"
                className="vd-btn vd-btn--primary"
                onClick={() => togglePanel('accept')}
                aria-expanded={panel === 'accept'}
              >
                <IconCheck size={16} /> Aceptar visita
              </button>
            )}
            {canMarkDone && (
              <button
                type="button"
                className="vd-btn vd-btn--primary"
                onClick={handleDone}
                disabled={actions.isSubmitting}
              >
                <IconCheck size={16} /> Marcar como realizada
              </button>
            )}
            <button
              type="button"
              className="vd-btn vd-btn--warning"
              onClick={() => togglePanel('schedule')}
              aria-expanded={panel === 'schedule'}
            >
              Reagendar
            </button>
            <button
              type="button"
              className="vd-btn vd-btn--danger"
              onClick={() => togglePanel('cancel')}
              aria-expanded={panel === 'cancel'}
            >
              {isPending ? 'Anular' : 'Cancelar'}
            </button>
          </div>
        )}
        {actions.error && panel === null && (
          <p className="vd-panel__error mt-3">{actions.error}</p>
        )}

        {canAct && panel === 'accept' && (
          <div className="vd-panel vd-panel--success vd-accept">
            {renderPanelHead(
              'Aceptar visita',
              'Define el tipo de visita y quién la atenderá.',
            )}
            <div className="vd-accept__grid">
              {renderVisitType()}
              {renderProperSelect()}
            </div>
            {renderDateTime('Cámbiala solo si hace falta.')}
            {actions.error && (
              <p className="vd-panel__error">{actions.error}</p>
            )}
            {renderFooter(
              'Confirmar y aceptar',
              handleAcceptClick,
              !properId || !actions.guestVisit,
            )}
          </div>
        )}

        {canAct && panel === 'schedule' && (
          <div className="vd-panel vd-panel--warning vd-accept">
            {renderPanelHead(
              'Reagendar visita',
              'Elige la nueva fecha y hora de la visita.',
            )}
            {isPending ? (
              <div className="vd-accept__grid">{renderVisitType()}</div>
            ) : (
              <div className="vd-accept__grid">{renderProperSelect()}</div>
            )}
            {renderDateTime()}
            {actions.error && (
              <p className="vd-panel__error">{actions.error}</p>
            )}
            {renderFooter(
              'Confirmar nueva cita',
              handleSchedule,
              !scheduleReady,
              'solid-warning',
            )}
          </div>
        )}

        {canAct && panel === 'cancel' && (
          <div className="vd-cancel">
            <h3 className="vd-cancel__title">
              {isPending ? 'Anular visita' : 'Cancelar visita'}
            </h3>
            {isPending ? (
              <p className="mb-0">
                ¿Estás seguro de anular la visita? Esta acción no se puede
                deshacer.
              </p>
            ) : (
              <>
                <select
                  className="vd-input"
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  aria-label="Motivo de la cancelación"
                >
                  <option value="">Selecciona un motivo</option>
                  {actions.cancellationReasons.map((reason) => (
                    <option key={reason} value={reason}>
                      {reason}
                    </option>
                  ))}
                </select>
                <textarea
                  className="vd-input"
                  rows={2}
                  value={cancelExplain}
                  onChange={(e) => setCancelExplain(e.target.value)}
                  placeholder="Comentario adicional (opcional)"
                />
              </>
            )}
            {actions.error && (
              <p className="vd-panel__error">{actions.error}</p>
            )}
            <div className="vd-cancel__actions">
              <button
                type="button"
                className="vd-btn vd-btn--solid-danger"
                onClick={handleCancel}
                disabled={
                  actions.isSubmitting || (!isPending && !cancelReason)
                }
              >
                {isPending ? 'Confirmar anulación' : 'Confirmar cancelación'}
              </button>
              <button
                type="button"
                className="vd-btn vd-btn--ghost"
                onClick={() => setPanel(null)}
              >
                Descartar
              </button>
            </div>
          </div>
        )}
      </section>

      <div className="vd-layout">
        <div className="vd-col">
          <Section icon={<IconUsers size={20} />} tone="blue" title="Inquilino">
            <div className="vd-grid">
              <Field label="Nombre">{visitInfo.tenant_name}</Field>
              <Field label="Correo">{visitInfo.tenant_email}</Field>
              <Field label="Teléfono">{visitInfo.tenant_phone}</Field>
              <Field label="Negocio">
                <VisitMarketplaceTypeBadge
                  marketplace_type={visitInfo.marketplace_type}
                />
              </Field>
            </div>
          </Section>

          <Section icon={<IconCalendar size={20} />} tone="green" title="Visita">
            <div className="vd-grid">
              <Field label="Fecha">{visitInfo.visit_date}</Field>
              <Field label="Hora">{visitInfo.visit_time}</Field>
              <Field label="Proper">{properInfo?.name}</Field>
              <Field label="SDR">{sdrInfo?.name}</Field>
              <Field label="Precio">{propertyInfo.price}</Field>
              <Field label="Tipo">
                {actions.guestVisit
                  ? actions.guestVisit.visitInfo.visit_type === 'presential'
                    ? 'Presencial'
                    : 'Virtual'
                  : ''}
              </Field>
            </div>
            <div className="vd-signals">
              <span className="vd-field__label">Señales de intención</span>
              <IntentSignalsCell
                favorites={visitInfo.tenant_favorites}
                visitDate={visitInfo.visit_date}
                visitTime={visitInfo.visit_time}
                status={status}
                onViewFavorites={() => setFavoritesOpen(true)}
              />
              {(visitInfo.tenant_favorites?.length ?? 0) > 0 && (
                <button
                  type="button"
                  className="vd-link"
                  onClick={() => setFavoritesOpen(true)}
                >
                  <IconHeart size={14} /> Ver favoritos
                </button>
              )}
            </div>
          </Section>
        </div>

        <div className="vd-col">
          <Section
            icon={<IconClock size={20} />}
            tone="orange"
            title="Estado de la visita"
          >
            {currentStep === -1 ? (
              <p className="mb-0">
                Esta visita está{' '}
                <strong>{getVisitStatusAdapter(status).toLowerCase()}</strong>.
              </p>
            ) : (
              <ol
                className={`vd-steps vd-steps--${getBadgeColorVisitStatusAdapter(
                  status,
                )}`}
              >
                {STEPS.map((step, index) => (
                  <li
                    key={step.key}
                    className={`vd-step${
                      index < currentStep ? ' is-done' : ''
                    }${index === currentStep ? ' is-current' : ''}`}
                  >
                    <span className="vd-step__dot">
                      {index < currentStep ? (
                        <IconCheck size={12} />
                      ) : (
                        index + 1
                      )}
                    </span>
                    <span className="vd-step__text">
                      <span className="vd-step__label">{step.label}</span>
                      {index === currentStep && (
                        <span className="vd-step__current">Estado actual</span>
                      )}
                    </span>
                  </li>
                ))}
              </ol>
            )}
          </Section>
          {isDone && (
            <VisitFeedbackCard
              state={
                visitInfo.has_feedback === false
                  ? 'pending'
                  : savedFeedback.state
              }
              feedback={savedFeedback.feedback}
              isSale={isSale}
            />
          )}
          <section className="vd-card vd-locked">
            <header className="vd-card__header">
              <span className="vd-icon vd-icon--muted">
                <IconLock size={20} />
              </span>
              <h2 className="vd-card__title">Reporte del propietario</h2>
            </header>
            <p className="vd-locked__text">
              Se habilita cuando la visita esté finalizada y se reciba el
              feedback del Proper y la respuesta del cliente.
            </p>
          </section>
        </div>
      </div>

      <InvestorFavoritesModal
        isOpen={favoritesOpen}
        toggle={() => setFavoritesOpen(false)}
        investorName={visitInfo.tenant_name}
        favorites={visitInfo.tenant_favorites || []}
        currentListingId={propertyInfo.id}
      />
      {actions.guestVisit && (
        <ActivateGuestModal
          guestId={actions.guestVisit.guestInfo.id}
          isOpen={activateOpen}
          onToggle={() => setActivateOpen(false)}
          onConfirmation={runAccept}
          bodyText={
            actions.guestVisit.guestInfo.is_register_as_tenant
              ? 'Al aceptar asignaras el lead al inquilino encontrado.'
              : 'Al activar el guest aceptas todas las ofertas, visitas del mismo y vuelves al guest un inquilino, Automáticamente enviaremos al inquilino un correo que genere su contraseña'
          }
          confirmationText="Entiendo, continuar"
          abortText="No, seguir revisando..."
          investor={actions.guestVisit}
        />
      )}
    </Container>
  );
};

export default RentVisitDetail;
